import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import creditApi from '../../api/creditApi';
import creditSubmissionApi, { CreditSubmission } from '../../api/creditSubmissionApi';
import debtorApi from '../../api/debtorApi';
import { useToast } from '../../context/ToastContext';

// Modular Components
import ApplicationListHeader from '../../components/credit/application/list/ApplicationListHeader';
import ApplicationListFilters from '../../components/credit/application/list/ApplicationListFilters';
import ApplicationTable from '../../components/credit/application/list/ApplicationTable';
import DeleteConfirmationDialog from '../../components/credit/application/list/DeleteConfirmationDialog';
import { createPageUrl } from '../../utils';

interface Application {
  id: string | number;
  application_number?: string;
  submission_number?: string;
  debtor_id?: string | number;
  credit_type?: string;
  requested_amount?: number;
  tenor_months?: number;
  total_score?: number;
  status: string;
}

export default function CreditApplicationList() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [applications, setApplications] = useState<Application[]>([]);
  const [debtors, setDebtors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [deleteId, setDeleteId] = useState<string | number | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      // Get data from both tables to show all data
      const [applicationsResponse, submissionsResponse, dbtrs] = await Promise.all([
        creditApi.getAll(),
        creditSubmissionApi.getAll().catch(() => []), // Fallback to empty array if API fails
        debtorApi.getAll(),
      ]);

      // Extract applications data (from credit_applications table)
      const applications = Array.isArray(applicationsResponse)
        ? applicationsResponse
        : Array.isArray((applicationsResponse as any)?.data)
          ? (applicationsResponse as any).data
          : [];

      // Extract submissions data (from credit_submissions table - includes the 2 data)
      const submissions: CreditSubmission[] = Array.isArray(submissionsResponse)
        ? submissionsResponse
        : Array.isArray((submissionsResponse as any)?.data)
          ? (submissionsResponse as any).data
          : [];

      // Transform credit_applications to Application format
      const transformedApplications: Application[] = applications.map((app: any) => ({
        id: `app_${app.id}`, // Prefix to avoid ID conflicts
        application_number: app.application_number || app.submission_number,
        submission_number: app.submission_number || app.application_number,
        debtor_id: app.debtor_id,
        credit_type: app.credit_type || '',
        requested_amount: app.requested_amount || 0,
        tenor_months: app.tenor_months || app.loan_term || 0,
        total_score: app.total_score || app.scoring_result || 0,
        status: app.status || 'draft',
      }));

      // Transform credit_submissions to Application format (includes the 2 data)
      const transformedSubmissions: Application[] = submissions.map((sub: CreditSubmission) => {
        // Extract tenor from financial_projection if available
        let tenorMonths = 0;
        if (sub.financial_projection && typeof sub.financial_projection === 'object') {
          tenorMonths = sub.financial_projection.tenor_bulan || 0;
        }

        return {
          id: `sub_${sub.id}`, // Prefix to avoid ID conflicts
          application_number: sub.submission_number,
          submission_number: sub.submission_number,
          debtor_id: sub.debtor_id,
          credit_type: sub.application_type || '',
          requested_amount: sub.requested_amount || 0,
          tenor_months: tenorMonths,
          total_score: 0, // TODO: Calculate from scoring model
          status: sub.status || 'draft',
        };
      });

      // Combine both sources - show all data from both tables
      const transformedApps = [...transformedApplications, ...transformedSubmissions];

      setApplications(transformedApps);
      setDebtors(dbtrs);
    } catch (error: any) {
      console.error('Error loading data:', error);
      console.error('Error details:', error?.response?.data);
      showError(error?.response?.data?.error || error?.message || 'Gagal memuat data aplikasi');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await creditApi.delete(Number(deleteId));
      setApplications(applications.filter(a => a.id !== deleteId));
      setDeleteId(null);
      showSuccess('Aplikasi kredit berhasil dihapus');
    } catch (error: any) {
      console.error('Error deleting application:', error);
      showError(error?.response?.data?.error || error?.message || 'Gagal menghapus aplikasi');
    }
  };

  const getDebtorName = (debtorId: string | number | undefined): string => {
    if (!debtorId) return '-';
    const id = typeof debtorId === 'string' ? parseInt(debtorId) : debtorId;
    return debtors.find(d => d.id === id)?.full_name || '-';
  };

  const filteredApplications = applications.filter(app => {
    const debtorName = getDebtorName(app.debtor_id);
    const appNumber = app.application_number || app.submission_number || '';
    const matchSearch =
      !searchTerm ||
      appNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      debtorName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === 'all' || app.status === filterStatus;
    const matchType = filterType === 'all' || app.credit_type === filterType;
    return matchSearch && matchStatus && matchType;
  });

  const handleEdit = (id: string | number) => {
    navigate(createPageUrl(`CreditSubmissionForm?id=${id}`));
  };

  const handleView = (id: string | number) => {
    // Extract actual ID (remove prefix if exists)
    const actualId = typeof id === 'string' && id.includes('_') ? id.split('_')[1] : id;
    navigate(`/credit/application/detail?id=${actualId}&from=application-list`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <ApplicationListHeader />

      {/* Filters */}
      <ApplicationListFilters
        searchTerm={searchTerm}
        filterStatus={filterStatus}
        filterType={filterType}
        onSearchChange={setSearchTerm}
        onStatusChange={setFilterStatus}
        onTypeChange={setFilterType}
      />

      {/* Table */}
      <ApplicationTable
        applications={filteredApplications}
        loading={loading}
        getDebtorName={getDebtorName}
        onEdit={handleEdit}
        onDelete={id => setDeleteId(id)}
        onView={handleView}
      />

      {/* Delete Confirmation */}
      <DeleteConfirmationDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
